import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, 
  Scale, 
  BookOpen, 
  Library, 
  Download,
  ExternalLink,
  Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'all', label: 'Tout', icon: Library },
  { id: 'juridique', label: 'Juridique', icon: Scale },
  { id: 'jurisprudence', label: 'Jurisprudence', icon: BookOpen },
  { id: 'guide', label: 'Guides', icon: FileText },
  { id: 'analyse', label: 'Analyses', icon: FileText },
  { id: 'bibliographie', label: 'Bibliographie', icon: Library }
];

export default function Documentation() {
  const [documents, setDocuments] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [guideLink, setGuideLink] = useState(null);

  useEffect(() => {
    fetchDocuments();
    fetchGuideLink();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGuideLink = async () => {
    try {
      const response = await axios.get(`${API}/settings/guide_link`);
      setGuideLink(response.data.value);
    } catch (error) {
      console.error('Failed to fetch guide link:', error);
      setGuideLink('#');
    }
  };

  const filteredDocuments = activeCategory === 'all' 
    ? documents 
    : documents.filter(doc => doc.category === activeCategory);

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.id === category);
    return cat ? cat.icon : FileText;
  };

  const isGuideAvailable = guideLink && guideLink !== '#';

  return (
    <div className="min-h-screen" data-testid="documentation-page">
      {/* Header */}
      <div className="bg-muted/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Documentation
          </h1>
          <p className="text-muted-foreground">
            Ressources juridiques, jurisprudences et guides pour monter en compétence.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Guide principal */}
        <Card className="mb-8 border-primary/20 bg-primary/5">
          <CardHeader>
            <div className="flex items-center gap-2 text-primary mb-2">
              <BookOpen className="w-5 h-5" />
              <Badge variant="secondary">Ressource principale</Badge>
            </div>
            <CardTitle className="text-2xl">Guide de défense juridique</CardTitle>
            <CardDescription className="text-base">
              Comment monter son dossier juridique et qu'est-ce que l'on doit mettre dedans pour se défendre au mieux ?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Ce guide complet vous accompagne pas à pas dans la constitution de votre dossier de défense. 
              Il aborde les documents essentiels, les preuves à rassembler et les stratégies à adopter.
            </p>
            {isGuideAvailable ? (
              <a 
                href={guideLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="main-guide-link"
              >
                <Button className="gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Consulter le guide
                </Button>
              </a>
            ) : (
              <Button className="gap-2" disabled data-testid="main-guide-link-disabled">
                <Clock className="w-4 h-4" />
                Disponible prochainement
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Categories Tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory}>
          <TabsList className="mb-6 flex-wrap h-auto gap-2">
            {categories.map((cat) => (
              <TabsTrigger 
                key={cat.id} 
                value={cat.id}
                className="gap-2"
                data-testid={`tab-${cat.id}`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeCategory}>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardHeader>
                      <div className="h-4 bg-muted rounded w-3/4" />
                      <div className="h-3 bg-muted rounded w-1/2 mt-2" />
                    </CardHeader>
                    <CardContent>
                      <div className="h-20 bg-muted rounded" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredDocuments.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Aucun document</h3>
                  <p className="text-muted-foreground">
                    Aucun document n'est disponible dans cette catégorie pour le moment.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredDocuments.map((doc) => {
                  const Icon = getCategoryIcon(doc.category);
                  return (
                    <Card key={doc.id} className="hover:shadow-md transition-shadow" data-testid={`doc-card-${doc.id}`}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="w-10 h-10 rounded-sm bg-accent flex items-center justify-center">
                            <Icon className="w-5 h-5 text-primary" />
                          </div>
                          <Badge variant="outline">{doc.file_type.toUpperCase()}</Badge>
                        </div>
                        <CardTitle className="text-lg mt-3">{doc.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="mb-4">{doc.description}</CardDescription>
                        {doc.file_url && doc.file_url !== '#' ? (
                          <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm" className="gap-2 w-full">
                              {doc.file_type === 'external' ? (
                                <>
                                  <ExternalLink className="w-4 h-4" />
                                  Consulter
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4" />
                                  Télécharger
                                </>
                              )}
                            </Button>
                          </a>
                        ) : (
                          <Button variant="outline" size="sm" className="gap-2 w-full" disabled>
                            <Download className="w-4 h-4" />
                            Bientôt disponible
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Info section */}
        <Card className="mt-12">
          <CardHeader>
            <CardTitle>Contribuer à la documentation</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Vous avez des ressources à partager ? Des jurisprudences favorables ? 
              Des analyses pertinentes ? Contactez-nous pour enrichir cette base documentaire 
              et aider d'autres personnes dans leur parcours de défense.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
